<template>
  <div class="landing-page">
    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-bg"></div>
      <div class="hero-computer">
        <img :src="heroComputerImg" :alt="$t('legacy.Макетта конструктор')" class="hero-computer-img" />
      </div>

      <!-- Header -->
      <header class="header">
        <div class="header-container">
          <router-link :to="localizedPath('/')" class="logo-link">
            <img :src="logoImg" :alt="$t('legacy.Макетта')" class="logo" />
          </router-link>
          <nav class="nav-menu">
            <a href="#about" class="nav-link">{{ $t('legacy.о сервисе') }}</a>
            <a href="#features" class="nav-link">{{ $t('legacy.возможности') }}</a>
            <a href="#pricing" class="nav-link">{{ $t('legacy.стоимость') }}</a>
            <a href="#examples" class="nav-link">{{ $t('legacy.примеры') }}</a>
          </nav>
          <div class="header-actions">
            <div class="header-buttons">
              <router-link :to="localizedPath('/register')" class="btn-primary-header">
                <span class="btn-text-full">{{ $t('legacy.попробовать бесплатно') }}</span>
                <span class="btn-text-mobile">{{ $t('legacy.Регистрация') }}</span>
              </router-link>
              <router-link :to="localizedPath('/login')" class="btn-secondary-header">{{ $t('legacy.Войти') }}</router-link>
            </div>
            <div class="language-switch" role="group" aria-label="Language">
              <button
                type="button"
                class="language-option"
                :class="{ 'language-option--active': currentLocale === 'ru' }"
                :aria-pressed="currentLocale === 'ru'"
                :aria-label="$t('legacy.Русский')"
                @click="switchLocale('ru')"
              >
                RU
              </button>
              <button
                type="button"
                class="language-option"
                :class="{ 'language-option--active': currentLocale === 'en' }"
                :aria-pressed="currentLocale === 'en'"
                aria-label="English"
                @click="switchLocale('en')"
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Hero Content -->
      <div class="hero-content-wrapper">
        <div class="hero-content">
          <div class="hero-text">
            <h1>{{ $t('legacy.Конструктор проектов') }}<br />{{ $t('legacy.для декораторов') }}<br />{{ $t('legacy.и ивент-специалистов') }}</h1>
            <p class="hero-subtitle">{{ $t('legacy.Создавайте уникальные композиции и сметы') }}<br />{{ $t('legacy.из сотен элементов без навыков дизайнера') }}</p>
          </div>
          <div class="hero-buttons">
            <router-link :to="localizedPath('/register')" class="btn-primary">{{ $t('legacy.попробовать бесплатно') }}</router-link>
            <a href="#about" class="btn-secondary">{{ $t('legacy.узнать больше') }}</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Benefits Section -->
    <section id="about" class="benefits-section">
      <h2 class="benefits-title">{{ $t('legacy.Вы получаете готовый инструмент,') }}<br />{{ $t('legacy.который поможет вам:') }}</h2>
      <div class="benefits-grid">
        <div class="benefit-card">
          <div class="benefit-icon benefit-icon-1">
            <img :src="benefit1Img" alt="" />
          </div>
          <div class="benefit-content">
            <h3>{{ $t('legacy.Получить конкурентное преимущество на рынке') }}</h3>
            <p>{{ $t('legacy.Создавайте реалистичные макеты, придумывайте новые комбинации для своих проектов, разрабатывайте каталоги и многое другое') }}</p>
          </div>
        </div>

        <div class="benefit-card">
          <div class="benefit-icon benefit-icon-2">
            <img :src="benefit2Img" alt="" />
          </div>
          <div class="benefit-content">
            <h3>{{ $t('legacy.Быстро нарисовать проект даже на телефоне') }}</h3>
            <p>{{ $t('legacy.В одном месте вы получаете интуитивный редактор с сотнями элементов декора') }}</p>
          </div>
        </div>

        <div class="benefit-card">
          <div class="benefit-icon benefit-icon-3">
            <img :src="benefit3Img" alt="" />
          </div>
          <div class="benefit-content">
            <h3>{{ $t('legacy.Не пользоваться услугами дизайнера') }}</h3>
            <p>{{ $t('legacy.Даже если вы никогда не рисовали эскизы, вы сможете сделать это легко без особых знаний других графических программ.') }}</p>
          </div>
        </div>

        <div class="benefit-card">
          <div class="benefit-icon benefit-icon-1">
            <img :src="benefit4Img" alt="" />
          </div>
          <div class="benefit-content">
            <h3>{{ $t('legacy.Экономить время на расчёте сметы проекта') }}</h3>
            <p>{{ $t('legacy.Теперь не нужно сто раз пересчитывать смету клиенту пытаясь уложиться в бюджет. Назначьте цены на позиции в каталоге и смотри как в калькуляторе меняется цена.') }}</p>
          </div>
        </div>

        <div class="benefit-card">
          <div class="benefit-icon benefit-icon-2">
            <img :src="benefit5Img" alt="" />
          </div>
          <div class="benefit-content">
            <h3>{{ $t('legacy.Повысить свою экспертность перед заказчиком') }}</h3>
            <p>{{ $t('legacy.Уже на встрече быстро нарисуйте проект, просто перенося элементы на поле и наблюдайте за автоматическим расчётом стоимости') }}</p>
          </div>
        </div>

        <div class="benefit-card">
          <div class="benefit-icon benefit-icon-3">
            <img :src="benefit6Img" alt="" />
          </div>
          <div class="benefit-content">
            <h3>{{ $t('legacy.Вести учёт ваших позиций декора на складе') }}</h3>
            <p>{{ $t('legacy.Сформируйте свой личный кабинет-склад, подключите календарь и отслеживайте наличие декора на выбранную дату') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <!-- Feature 1: Image Left, Text Right -->
      <div class="feature-row">
        <div class="feature-media feature-media--full">
          <img :src="feature1Img" alt="DELPER interface" />
        </div>
        <div class="feature-text">
          <h3><strong>{{ $t('legacy.Макетта') }}</strong> {{ $t('legacy.создан с заботой о декораторах, чтобы облегчить им рабочие процессы') }}</h3>
          <p>{{ $t('legacy.В личном кабинете у вас будет множество фонов и элементов для создания уникальных композиций.') }}</p>
          <p>{{ $t('legacy.Интуитивный редактор позволяет изменять их размеры, зеркалить, вращать и так далее.') }}</p>
        </div>
      </div>

      <!-- Feature 2: Text Left, Image Right -->
      <div class="feature-row feature-row-3d">
        <div class="feature-text">
          <h3>{{ $t('legacy.Визуализация с помощью 3D-рендеринга') }}</h3>
          <p>{{ $t('legacy.Инструмент «ИИ Реализм» поможет вдохнуть жизнь в проект.') }}</p>
          <p>{{ $t('legacy.Вы сможете в одно касание получить реалистичную визуализацию.') }}</p>
        </div>
        <div class="feature-media">
          <img :src="feature2Img" alt="3D rendering" />
        </div>
      </div>

      <!-- Feature 3: Image Left, Text Right -->
      <div class="feature-row">
        <div class="feature-media feature-media--full">
          <img :src="feature3Img" alt="Additional functions" />
        </div>
        <div class="feature-text">
          <h3>{{ $t('legacy.Быстрый расчёт стоимости проекта') }}</h3>
          <p>{{ $t('legacy.Сохранённые композиции остаются доступными в личном кабинете, где их можно доработать в любой момент.') }}</p>
          <p>{{ $t('legacy.А такие функции как калькулятор, подключённый календарь и вывод сметы — сэкономят вам время работы и значительно облегчат рутинные дела') }}</p>
        </div>
      </div>
    </section>

    <!-- Demo Section -->
    <section class="demo-section">
      <div class="demo-content">
        <h2>{{ $t('legacy.Попробуйте наш бесплатный') }}<br />{{ $t('legacy.демо-режим') }}</h2>
        <p class="demo-subtitle">{{ $t('legacy.Чтобы ознакомиться с основными функциями и возможностями конструктора') }}</p>
        <router-link :to="localizedPath('/register')" class="btn-primary demo-btn">{{ $t('legacy.попробовать бесплатно') }}</router-link>
      </div>
      <div class="demo-media">
        <div class="demo-video-wrapper">
          <video
            ref="demoVideo"
            class="demo-video"
            loop
            playsinline
            autoplay
            muted
            :poster="feature1Img"
          ></video>
          <button
            class="demo-sound-btn"
            @click="toggleDemoSound"
            :aria-label="demoMuted ? $t('legacy.Включить звук') : $t('legacy.Выключить звук')"
          >
            <svg v-if="demoMuted" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="22" height="22">
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="22" height="22">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          </button>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section">
      <h2 class="section-title">{{ $t('legacy.Тарифы') }}</h2>
      <div class="pricing-grid pricing-grid-3">
        <div class="pricing-card">
          <div class="pricing-header">
            <h3 class="pricing-name">{{ $t('legacy.Бесплатный') }}</h3>
            <p class="pricing-price">0 <span>{{ currentLocale === 'en' ? '$' : $t('legacy.руб') }}</span></p>
          </div>
          <div class="pricing-content">
            <ul class="pricing-features">
              <li>{{ $t('legacy.Доступ к конструктору') }}</li>
              <li>{{ $t('legacy.Сокращенная библиотека элементов') }}</li>
              <li>{{ $t('legacy.Сохранение проектов') }}</li>
            </ul>
          </div>
          <router-link :to="localizedPath('/register')" class="btn-pricing btn-pricing-outline">{{ $t('legacy.Попробовать') }}</router-link>
        </div>

        <template v-if="plansLoading">
          <div class="pricing-card">
            <div class="pricing-header pricing-header-base">
              <h3 class="pricing-name">Base</h3>
              <p class="pricing-price"><span class="skeleton-text skeleton-price"></span></p>
            </div>
            <div class="pricing-content">
              <ul class="pricing-features">
                <li><span class="skeleton-text skeleton-line"></span></li>
                <li><span class="skeleton-text skeleton-line"></span></li>
                <li><span class="skeleton-text skeleton-line"></span></li>
                <li><span class="skeleton-text skeleton-line"></span></li>
              </ul>
            </div>
            <span class="btn-pricing btn-pricing-filled btn-disabled">{{ $t('legacy.Купить') }}</span>
          </div>
          <div class="pricing-card pricing-card-featured">
            <div class="pricing-popular-badge">{{ $t('legacy.Популярный') }}</div>
            <div class="pricing-header pricing-header-premium">
              <h3 class="pricing-name">Premium</h3>
              <p class="pricing-price"><span class="skeleton-text skeleton-price"></span></p>
            </div>
            <div class="pricing-content">
              <ul class="pricing-features">
                <li><span class="skeleton-text skeleton-line"></span></li>
                <li><span class="skeleton-text skeleton-line"></span></li>
                <li><span class="skeleton-text skeleton-line"></span></li>
                <li><span class="skeleton-text skeleton-line"></span></li>
                <li><span class="skeleton-text skeleton-line"></span></li>
              </ul>
            </div>
            <span class="btn-pricing btn-pricing-premium btn-disabled">{{ $t('legacy.Купить') }}</span>
          </div>
        </template>
        <template v-else>
          <div class="pricing-card">
            <div class="pricing-header pricing-header-base">
              <h3 class="pricing-name">Base</h3>
              <p v-if="getPlanPrice('base') !== null" class="pricing-price">{{ formatPrice('base') }} <span>{{ pricePeriod }}</span></p>
              <p v-else class="pricing-unavailable">{{ priceUnavailable }}</p>
            </div>
            <div class="pricing-content">
              <ul class="pricing-features">
                <li>{{ $t('legacy.Доступ к конструктору') }}</li>
                <li>{{ $t('legacy.Полная библиотека элементов') }}</li>
                <li>{{ $t('legacy.Экспорт без водяного знака') }}</li>
                <li>{{ $t('legacy.Добавление своих элементов в библиотеку') }}</li>
                <li>{{ $t('subscription.customFonts') }}</li>
                <li>{{ $t('legacy.Функция ИИ реализм') }} ({{ $t('legacy.в подарок') }} {{ getPlanCredits('base') }} {{ $t('legacy.обработки') }})</li>
              </ul>
            </div>
            <button class="btn-pricing btn-pricing-filled" :disabled="getPlanPrice('base') === null" @click="handleBuyPlan('base')">{{ $t('legacy.Купить') }}</button>
          </div>

          <div class="pricing-card pricing-card-featured">
            <div class="pricing-popular-badge">{{ $t('legacy.Популярный') }}</div>
            <div class="pricing-header pricing-header-premium">
              <h3 class="pricing-name">Premium</h3>
              <p v-if="getPlanPrice('premium') !== null" class="pricing-price">{{ formatPrice('premium') }} <span>{{ pricePeriod }}</span></p>
              <p v-else class="pricing-unavailable">{{ priceUnavailable }}</p>
            </div>
            <div class="pricing-content">
              <ul class="pricing-features">
                <li>{{ $t('legacy.Доступ к конструктору') }}</li>
                <li>{{ $t('legacy.Полная библиотека элементов') }}</li>
                <li>{{ $t('legacy.Экспорт без водяного знака') }}</li>
                <li>{{ $t('legacy.Добавление своих элементов в библиотеку') }}</li>
                <li>{{ $t('subscription.customFonts') }}</li>
                <li>{{ $t('legacy.Калькулятор проекта') }}</li>
                <li>{{ $t('legacy.Экспорт сметы в PDF или Excel') }}</li>
                <li>NEW! {{ $t('legacy.Функция ИИ реализм') }} ({{ getPlanCredits('premium') }} {{ $t('legacy.обработки') }})</li>
                <li>{{ $t('legacy.приоритетная поддержка') }}</li>
              </ul>
            </div>
            <button class="btn-pricing btn-pricing-premium" :disabled="getPlanPrice('premium') === null" @click="handleBuyPlan('premium')">{{ $t('legacy.Купить') }}</button>
          </div>
        </template>
      </div>
    </section>

    <!-- Examples Section -->
    <section id="examples" class="examples-section">
      <div class="examples-header">
        <h2 class="section-title">{{ $t('legacy.Примеры созданных композиций') }}</h2>
        <p class="section-subtitle">{{ $t('legacy.Познакомьтесь с примерами композиций, которые созданы нашими пользователями с помощью конструктора «') }}<strong>{{ $t('legacy.Макетта') }}</strong>{{ $t('legacy.». Это настоящие проекты, которые вдохновляют на создание оформления для любых событий — от элегантных свадеб до роскошных корпоративных вечеров.') }}</p>
        <div class="slider-buttons">
          <button class="slider-btn" @click="scrollExamples(-1)">
            <img :src="arrowLeftImg" :alt="$t('legacy.Предыдущее')" />
          </button>
          <button class="slider-btn" @click="scrollExamples(1)">
            <img :src="arrowRightImg" :alt="$t('legacy.Следующее')" />
          </button>
        </div>
      </div>
      <div class="examples-slider" ref="examplesSlider">
        <div class="example-card" v-for="(img, idx) in exampleImages" :key="idx" @click="openLightbox(idx)">
          <img :src="img" :alt="$t('legacy.Пример композиции {index}', { index: idx + 1 })" loading="lazy" />
          <div class="example-card-overlay">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M15 3H21M21 3V9M21 3L13 11M9 21H3M3 21V15M3 21L11 13" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </section>

    <!-- Lightbox -->
    <Teleport to="body">
      <div v-if="lightboxOpen" class="lightbox-overlay" @click.self="closeLightbox">
        <button class="lightbox-close" @click="closeLightbox" :aria-label="$t('common.close')">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
          </svg>
        </button>

        <button class="lightbox-nav lightbox-prev" @click="lightboxPrev" :disabled="exampleImages.length <= 1" :aria-label="$t('legacy.Предыдущее')">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="lightbox-content" @click.stop>
          <img :src="exampleImages[lightboxIndex]" :alt="$t('legacy.Пример композиции {index}', { index: lightboxIndex + 1 })" class="lightbox-img" />
        </div>

        <button class="lightbox-nav lightbox-next" @click="lightboxNext" :disabled="exampleImages.length <= 1" :aria-label="$t('legacy.Следующее')">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <div class="lightbox-counter">{{ lightboxIndex + 1 }} / {{ exampleImages.length }}</div>
      </div>
    </Teleport>

    <!-- Reviews Section -->
    <section class="reviews-section">
      <div class="reviews-header">
        <h2 class="section-title">{{ $t('legacy.Отзывы тех,') }}<br />{{ $t('legacy.кто уже попробовал') }} <strong>{{ $t('legacy.Макетта') }}</strong></h2>
        <div class="slider-buttons">
          <button class="slider-btn" @click="scrollReviews(-1)">
            <img :src="arrowLeftImg" :alt="$t('legacy.Предыдущее')" />
          </button>
          <button class="slider-btn" @click="scrollReviews(1)">
            <img :src="arrowRightImg" :alt="$t('legacy.Следующее')" />
          </button>
        </div>
      </div>
      <div class="reviews-slider" ref="reviewsSlider">
        <div class="review-card">
          <div class="review-author">
            <img :src="avatar1Img" alt="Avatar" class="author-avatar" />
            <div class="author-info">
              <p class="author-name">{{ $t('legacy.Екатерина Волкова') }}</p>
              <p class="author-role">{{ $t('legacy.декоратор, Москва') }}</p>
            </div>
          </div>
          <p class="review-text"><strong>{{ $t('legacy.Макетта') }}</strong> {{ $t('legacy.полностью изменил мой подход к работе с клиентами! Раньше на создание эскиза уходило несколько часов, теперь — 15 минут прямо на встрече. Калькулятор автоматически считает стоимость, и заказчик сразу видит итоговую смету. Экономлю кучу времени!') }}</p>
        </div>
        <div class="review-card">
          <div class="review-author">
            <img :src="avatar2Img" alt="Avatar" class="author-avatar" />
            <div class="author-info">
              <p class="author-name">{{ $t('legacy.Дмитрий Соколов') }}</p>
              <p class="author-role">{{ $t('legacy.ивент-менеджер, Казань') }}</p>
            </div>
          </div>
          <p class="review-text">{{ $t('legacy.Функция «ИИ Реализм» — это что-то невероятное! Показываю клиентам реалистичную визуализацию в одно касание, и они сразу понимают, как будет выглядеть площадка. Конверсия в заказы выросла в два раза. Рекомендую всем коллегам!') }}</p>
        </div>
        <div class="review-card">
          <div class="review-author">
            <img :src="avatar3Img" alt="Avatar" class="author-avatar" />
            <div class="author-info">
              <p class="author-name">{{ $t('legacy.Анна Морозова') }}</p>
              <p class="author-role">{{ $t('legacy.свадебный декоратор, Новосибирск') }}</p>
            </div>
          </div>
          <p class="review-text">{{ $t('legacy.Удобно, что можно загружать свои элементы и формировать личный каталог. Все мои арки, стойки и цветочные композиции теперь в одном месте с ценами. Экспорт сметы в PDF выглядит профессионально — клиенты в восторге!') }}</p>
        </div>
        <div class="review-card">
          <div class="review-author">
            <img :src="avatar4Img" alt="Avatar" class="author-avatar" />
            <div class="author-info">
              <p class="author-name">{{ $t('legacy.Сергей Кузнецов') }}</p>
              <p class="author-role">{{ $t('legacy.владелец декор-студии, Екатеринбург') }}</p>
            </div>
          </div>
          <p class="review-text">{{ $t('legacy.Пользуюсь') }} <strong>{{ $t('legacy.Макетта') }}</strong> {{ $t('legacy.уже полгода. Особенно ценю возможность работать с телефона — могу набросать проект где угодно. Библиотека элементов огромная, а интерфейс настолько простой, что разобрался за 10 минут. Отличный инструмент для профессионалов!') }}</p>
        </div>
        <div class="review-card">
          <div class="review-author">
            <img :src="avatar5Img" alt="Avatar" class="author-avatar" />
            <div class="author-info">
              <p class="author-name">{{ $t('legacy.Ольга Белова') }}</p>
              <p class="author-role">{{ $t('legacy.флорист-декоратор, Санкт-Петербург') }}</p>
            </div>
          </div>
          <p class="review-text">{{ $t('legacy.Наконец-то нашла сервис, который понимает специфику нашей работы! Зеркалирование, вращение, группировка элементов — всё продумано. А функция избранного помогает быстро находить часто используемые элементы. Спасибо команде') }} <strong>{{ $t('legacy.Макетта') }}</strong>!</p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section" :style="{ backgroundImage: `url(${footerBgImg})` }">
      <div class="cta-content">
        <h2>{{ $t('legacy.Готовы улучшить свою работу?') }}<br />{{ $t('legacy.Оформите подписку на DELPER и получите доступ к полному функционалу') }}</h2>
        <p class="cta-subtitle">{{ $t('legacy.Попробуйте демо-версию бесплатно, а затем перейдите на полную, чтобы получить доступ ко всем инструментам и эксклюзивным элементам для создания по-настоящему уникальных композиций.') }}</p>
        <div class="cta-buttons">
          <router-link :to="localizedPath('/register')" class="btn-primary">{{ $t('legacy.попробовать бесплатно') }}</router-link>
        </div>
      </div>

      <!-- Footer -->
      <footer class="footer">
        <div class="footer-top">
          <div class="footer-brand">
            <img :src="logoWhiteImg" :alt="$t('legacy.Макетта')" class="footer-logo" />
            <div class="footer-company">
              <p>2025 — {{ currentYear }}</p>
              <p>{{ $t('legacy.ИП Немыкина Екатерина Сергеевна') }}</p>
              <p>{{ $t('legacy.ОГРНИП 325547600094265') }}</p>
              <p>{{ $t('legacy.ИНН 421717178949') }}</p>
            </div>
          </div>
          <div class="footer-nav">
            <nav class="footer-links">
              <a href="#about">{{ $t('legacy.О сервисе') }}</a>
              <a href="#features">{{ $t('legacy.Возможности') }}</a>
              <a href="#pricing">{{ $t('legacy.Стоимость') }}</a>
              <a href="#examples">{{ $t('legacy.Примеры') }}</a>
            </nav>
            <nav class="footer-links footer-legal">
              <router-link to="/terms">{{ $t('legacy.Публичная оферта') }}</router-link>
              <router-link to="/refund-policy">{{ $t('legacy.Политика возврата') }}</router-link>
              <router-link to="/privacy-policy">{{ $t('legacy.Политика конфиденциальности') }}</router-link>
              <router-link to="/personal-data-consent">{{ $t('legacy.Согласие на обработку ПД') }}</router-link>
              <router-link to="/contacts">{{ $t('legacy.Контакты') }}</router-link>
            </nav>
            <div class="footer-contact">
              <router-link :to="localizedPath('/login')" class="btn-secondary-header footer-login">{{ $t('legacy.Войти') }}</router-link>
              <p class="contact-label">{{ $t('legacy.Остались вопросы,') }}<br />{{ $t('legacy.напишите нам') }}</p>
              <a href="mailto:maketta.pro@mail.ru" class="contact-email">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                maketta.pro@mail.ru
              </a>
              <p class="contact-label">{{ $t('legacy.Инструкции и комьюнити:') }}</p>
              <a href="https://vk.ru/makettapro" target="_blank" rel="noopener noreferrer" class="btn-secondary-header footer-login contact-vk">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M13.162 18.994c.609 0 .858-.406.851-.915-.031-1.917.714-2.949 2.059-1.604 1.488 1.488 1.796 2.519 3.603 2.519h3.2c.808 0 1.126-.26 1.126-.668 0-.863-1.421-2.386-2.625-3.504-1.686-1.565-1.765-1.602-.313-3.486 1.801-2.339 4.157-5.336 2.073-5.336h-3.981c-.772 0-.828.435-1.103 1.083-.995 2.347-2.886 5.387-3.604 4.922-.751-.485-.407-2.406-.35-5.261.015-.754.011-1.271-1.141-1.539-.629-.145-1.241-.205-1.809-.205-2.273 0-3.841.953-2.95 1.119 1.571.293 1.42 3.692 1.054 5.16-.638 2.556-3.036-2.024-4.035-4.305-.241-.548-.315-.974-1.175-.974h-3.255c-.492 0-.787.16-.787.516 0 .602 2.96 6.72 5.786 9.77 2.756 2.975 5.48 2.708 7.376 2.708z"/></svg>
                {{ $t('legacy.ВКонтакте') }}
              </a>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <router-link to="/terms" class="footer-bottom-link">{{ $t('legacy.Публичная оферта') }}</router-link>
          <router-link to="/refund-policy" class="footer-bottom-link">{{ $t('legacy.Политика возврата') }}</router-link>
          <router-link to="/privacy-policy" class="footer-bottom-link">{{ $t('legacy.Политика конфиденциальности') }}</router-link>
          <router-link to="/personal-data-consent" class="footer-bottom-link">{{ $t('legacy.Согласие на обработку ПД') }}</router-link>
          <p>© 2025 — {{ currentYear }} <strong>{{ $t('legacy.Макетта') }}</strong>. {{ $t('legacy.Все права защищены') }}</p>
        </div>
      </footer>
    </section>
    
    <AuthModal 
      v-if="showAuthModal" 
      @close="closeAuthModal" 
      @success="handleAuthSuccess" 
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AuthModal from '@/components/auth/AuthModal.vue';
import { getSubscriptionPlans } from '@/api/subscription';
import { setLocale } from '@/i18n';

export default {
  name: 'LandingPage',
  components: {
    AuthModal
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      backgroundImg: '/landing/background.svg',
      decorationImg: '/landing/decoration.svg',
      heroComputerImg: '/landing/hero-mockup.png',
      logoImg: '/landing/icon.svg',
      benefit1Img: '/landing/benefit1.svg',
      benefit2Img: '/landing/benefit2.svg',
      benefit3Img: '/landing/benefit3.svg',
      benefit4Img: '/landing/benefit4.svg',
      benefit5Img: '/landing/benefit5.svg',
      benefit6Img: '/landing/benefit6.svg',
      feature1Img: '/landing/hero-screen.jpg',
      feature2Img: '/landing/feature2.png',
      feature3Img: '/landing/feature1.png',
      playButtonImg: '/landing/play-button.png',
      exampleImages: Array.from({ length: 11 }, (_, i) => `/landing/examples/example${i + 1}.jpg`),
      lightboxOpen: false,
      lightboxIndex: 0,
      avatar1Img: '/landing/avatar1.png',
      avatar2Img: '/landing/avatar2.png',
      avatar3Img: '/landing/avatar3.png',
      avatar4Img: '/landing/avatar4.png',
      avatar5Img: '/landing/avatar5.png',
      arrowLeftImg: '/landing/arrow-left.svg',
      arrowRightImg: '/landing/arrow-right.svg',
      logoWhiteImg: '/landing/logo-white.svg',
      telegramImg: '/landing/telegram.svg',
      footerBgImg: '/landing/footer-bg.png',
      demoMuted: true,
      showAuthModal: false,
      pendingPlan: null,
      subscriptionPlans: [],
      plansLoading: true
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    pricePeriod() {
      return this.currentLocale === 'en' ? '$/month' : this.$t('legacy.руб/мес');
    },
    priceUnavailable() {
      return this.$t(this.currentLocale === 'en' ? 'pricing.unavailable' : 'pricing.rubUnavailable');
    },
    currentLocale() {
      const path = this.$route?.path || '/';
      return path === '/en' || path.startsWith('/en/') ? 'en' : 'ru';
    }
  },
  watch: {
    currentLocale: {
      immediate: true,
      handler(locale) {
        setLocale(locale);
      }
    }
  },
  async mounted() {
    await this.loadSubscriptionPlans();
    if (document.readyState === 'complete') {
      this.$nextTick(() => this.loadDemoVideo());
    } else {
      window.addEventListener('load', () => this.loadDemoVideo(), { once: true });
    }
    window.addEventListener('keydown', this.onKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
    document.body.style.overflow = '';
  },
  methods: {
    localizedPath(path) {
      if (this.currentLocale !== 'en') return path;
      return path === '/' ? '/en' : `/en${path}`;
    },
    switchLocale(locale) {
      setLocale(locale);
      const path = locale === 'en' ? '/en' : '/';
      if (this.$route?.path !== path) {
        this.$router.push(path);
      }
    },
    loadDemoVideo() {
      const video = this.$refs.demoVideo;
      if (video && !video.src) {
        video.src = '/landing/demo.mp4';
        video.load();
        video.play().catch(() => {});
      }
    },
    toggleDemoSound() {
      this.demoMuted = !this.demoMuted;
      const video = this.$refs.demoVideo;
      if (video) {
        video.muted = this.demoMuted;
      }
    },
    openLightbox(idx) {
      this.lightboxIndex = idx;
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden';
    },
    closeLightbox() {
      this.lightboxOpen = false;
      document.body.style.overflow = '';
    },
    lightboxPrev() {
      this.lightboxIndex = (this.lightboxIndex - 1 + this.exampleImages.length) % this.exampleImages.length;
    },
    lightboxNext() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.exampleImages.length;
    },
    onKeyDown(e) {
      if (!this.lightboxOpen) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.lightboxPrev();
      if (e.key === 'ArrowRight') this.lightboxNext();
    },
    scrollExamples(direction) {
      const slider = this.$refs.examplesSlider;
      if (slider) {
        slider.scrollBy({ left: direction * 470, behavior: 'smooth' });
      }
    },
    scrollReviews(direction) {
      const slider = this.$refs.reviewsSlider;
      if (slider) {
        slider.scrollBy({ left: direction * 400, behavior: 'smooth' });
      }
    },
    async loadSubscriptionPlans() {
      this.plansLoading = true;
      try {
        this.subscriptionPlans = await getSubscriptionPlans();
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        this.subscriptionPlans = [];
      } finally {
        this.plansLoading = false;
      }
    },
    getPlanPrice(plan) {
      const planData = this.subscriptionPlans.find(p => p.id === plan);
      const value = this.currentLocale === 'en' ? planData?.priceUsd : planData?.price;
      if (value === null || value === undefined || value === '') return null;
      return Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null;
    },
    formatPrice(plan) {
      const price = this.getPlanPrice(plan);
      if (price === null) return '—';
      return this.$formatNumber(price, { maximumFractionDigits: 2 });
    },
    getPlanCredits(plan) {
      const planData = this.subscriptionPlans.find(p => p.id === plan);
      return planData?.aiCredits || '...';
    },
    handleBuyPlan(plan) {
      const price = this.getPlanPrice(plan);
      if (price === null) return;
      if (this.isAuthenticated) {
        this.$router.push({ path: '/subscription/payment', query: { plan, price } });
      } else {
        this.pendingPlan = plan;
        localStorage.setItem('pendingPlan', plan);
        localStorage.setItem('pendingPlanPrice', price);
        this.showAuthModal = true;
      }
    },
    handleAuthSuccess() {
      this.showAuthModal = false;
      const planToUse = this.pendingPlan || localStorage.getItem('pendingPlan');
      const priceToUse = localStorage.getItem('pendingPlanPrice') || this.getPlanPrice(planToUse);
      if (planToUse) {
        localStorage.removeItem('pendingPlan');
        localStorage.removeItem('pendingPlanPrice');
        this.pendingPlan = null;
        this.$router.push({ path: '/subscription/payment', query: { plan: planToUse, price: priceToUse } });
      } else {
        this.$router.push({ path: '/projects' });
      }
    },
    closeAuthModal() {
      this.showAuthModal = false;
      this.pendingPlan = null;
      localStorage.removeItem('pendingPlan');
      localStorage.removeItem('pendingPlanPrice');
    }
  }
}
</script>

<style lang="scss" scoped>
.landing-page {
  width: 100%;
  min-height: 100vh;
  background: #fff;
  font-family: 'Montserrat', sans-serif;
  overflow-x: hidden;
}

.hero-section {
  position: relative;
  height: 834px;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: url('~@/../public/landing/hero-bg.jpg') no-repeat center top;
  background-size: cover;
  z-index: 0;
}

.hero-computer {
  position: absolute;
  right: calc(50% - 720px);
  top: 50%;
  transform: translateY(-50%);
  width: 630px;
  height: 490px;
  z-index: 1;
}

@media (max-width: 1600px) {
  .hero-computer {
    right: 0;
  }
}

.hero-computer-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}


.header {
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 28px 60px;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
}

.logo-link {
  flex-shrink: 0;
}

.logo {
  width: 136px !important;
  height: 50px !important;
  object-fit: contain;
  display: block;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
}

.nav-link {
  font-weight: 500;
  font-size: 14px;
  line-height: 1.2;
  text-transform: uppercase;
  color: #fff;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.header-buttons {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.language-switch {
  display: flex;
  align-items: center;
  height: 42px;
  padding: 3px;
  gap: 2px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 9999px;
}

.language-option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #fff;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.35px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  &--active {
    background: #fff;
    color: #496782;
  }
}

.btn-text-full {
  display: inline;
}

.btn-text-mobile {
  display: none;
}

.btn-primary-header {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 40px;
  background: #496782;
  border-radius: 9999px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  color: #fff;
  text-decoration: none;
  letter-spacing: 0.35px;
  transition: background 0.2s;
  white-space: nowrap;
  text-align: center;

  &:hover {
    background: #3a5569;
  }
}

.btn-secondary-header {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 42px;
  background: #fff;
  border: 2px solid #e0e4e8;
  border-radius: 9999px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  color: #496782;
  text-decoration: none;
  letter-spacing: 0.35px;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: #f5f7f9;
    border-color: #c5ccd3;
  }
}

.hero-content-wrapper {
  position: relative;
  z-index: 5;
  width: 100%;
  padding: 0 60px;
}

.hero-content {
  display: flex;
  flex-direction: column;
  gap: 70px;
  padding-top: 116px;
  max-width: 1440px;
  margin: 0 auto;
}

.hero-content .hero-text {
  max-width: 620px;
}

.hero-text {
  display: flex;
  flex-direction: column;
  gap: 30px;
  color: #fff;

  h1 {
    font-weight: 600;
    font-size: 48px;
    line-height: 1.1;
    margin: 0;
  }
}

.hero-subtitle {
  font-weight: 400;
  font-size: 22px;
  line-height: 1.2;
  margin: 0;
}

.hero-buttons {
  display: flex;
  gap: 20px;
}

.btn-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 0 40px;
  min-width: 320px;
  background: #496782;
  border-radius: 9999px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  font-weight: 600;
  font-size: 16px;
  text-transform: uppercase;
  color: #fff;
  text-decoration: none;
  transition: background 0.2s;
  white-space: nowrap;
  text-align: center;

  &:hover {
    background: #3a5569;
  }
}

.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 0 42px;
  min-width: 220px;
  background: #fff;
  border: 2px solid #e0e4e8;
  border-radius: 9999px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  font-weight: 600;
  font-size: 16px;
  text-transform: uppercase;
  color: #496782;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s;
  white-space: nowrap;
  text-align: center;

  &:hover {
    background: #f5f7f9;
    border-color: #c5ccd3;
  }
}

.benefits-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 50px;
  padding: 80px 60px;
  background: #fff;
}

.benefits-title {
  font-weight: 500;
  font-size: 50px;
  line-height: 1.2;
  text-align: center;
  color: #26374e;
  margin: 0;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1400px;
  width: 100%;
}

.benefit-card {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 30px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.benefit-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 15px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);

  img {
    width: 56px;
    height: 56px;
    object-fit: contain;
  }
}

.benefit-icon-1 {
  background: linear-gradient(to bottom, #496782, #27374e);
}

.benefit-icon-2 {
  background: linear-gradient(to bottom, #8299ae, #496782);
}

.benefit-icon-3 {
  background: linear-gradient(to bottom, #bbcad8, #8299ae);
}

.benefit-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  line-height: 1.4;

  h3 {
    font-weight: 600;
    font-size: 22px;
    color: #26374e;
    margin: 0;
  }

  p {
    font-weight: 400;
    font-size: 16px;
    color: #496782;
    margin: 0;
  }
}

// Features Section
.features-section {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 80px;
  padding: 80px 80px;
  background: linear-gradient(180deg, #fff 0%, #e8f0f7 50%, #fff 100%);
}

.feature-row {
  display: flex;
  align-items: center;
  gap: 85px;
  max-width: 1400px;
  margin: 0 auto;
}

.feature-row-reverse {
  flex-direction: row-reverse;
}

.feature-media {
  position: relative;
  flex-shrink: 0;
  width: 640px;
  height: 320px;
  border: 15px solid #fff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.feature-media--full {
  height: auto;
  overflow: visible;

  img {
    height: auto;
    object-fit: contain;
  }
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
  }
}

.feature-text {
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 550px;

  h3 {
    font-weight: 600;
    font-size: 30px;
    line-height: 1.4;
    color: #26374e;
    margin: 0;
  }

  p {
    font-weight: 400;
    font-size: 16px;
    line-height: 1.4;
    color: #496782;
    margin: 0;
  }
}

// Demo Section
.demo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 50px;
  padding: 80px 60px;
  background: #fff;
}

.demo-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  text-align: center;

  h2 {
    font-weight: 600;
    font-size: 50px;
    line-height: 1.2;
    color: #26374e;
    margin: 0;
  }
}

.demo-subtitle {
  font-weight: 400;
  font-size: 20px;
  color: #1a1a1a;
  margin: 0;
}

.demo-btn {
  width: 320px;
}

.demo-media {
  width: 100%;
  max-width: 900px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: visible;
}

.demo-video-wrapper {
  position: relative;
  width: 100%;
  line-height: 0;
}

.demo-video {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 20px;
}

.demo-sound-btn {
  position: absolute;
  bottom: 14px;
  right: 14px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  transition: background 0.2s;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.65);
  }

  svg {
    display: block;
    flex-shrink: 0;
  }
}

// Pricing Section
.pricing-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 50px;
  padding: 80px 60px;
  background: #fff;
}

.section-title {
  font-weight: 600;
  font-size: 50px;
  line-height: 1.2;
  color: #26374e;
  text-align: center;
  margin: 0;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  max-width: 1320px;
  width: 100%;
}

.pricing-grid-2 {
  grid-template-columns: repeat(2, 1fr);
  max-width: 800px;
}

.pricing-grid-3 {
  grid-template-columns: repeat(3, 1fr);
  max-width: 100%;
}

.pricing-header-base {
  background: linear-gradient(135deg, #e8eef4 0%, #d9e3ed 100%);
}

.pricing-popular-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #496782, #5a7a99);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 20px;
  white-space: nowrap;
  z-index: 10;
}

.btn-pricing-outline {
  background: transparent;
  border: 2px solid #496782;
  color: #496782;
}

.btn-pricing-outline:hover {
  background: #496782;
  color: #fff;
}

.pricing-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  min-height: 380px;
  overflow: hidden;
}

.pricing-card-featured {
  position: relative;
  overflow: visible;
  box-shadow: 0 10px 40px rgba(73, 103, 130, 0.5);
}

.pricing-header {
  background: linear-gradient(135deg, #e8eef4 0%, #d9e3ed 100%);
  padding: 25px 30px;
  text-align: center;
}

.pricing-header-premium {
  background: linear-gradient(135deg, #496782 0%, #26374e 100%);
  border-radius: 24px 24px 0 0;
}

.pricing-header-premium .pricing-name,
.pricing-header-premium .pricing-price,
.pricing-header-premium .pricing-price span {
  color: #fff;
}

.pricing-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  flex-grow: 1;
  margin-bottom: 40px;
}

.pricing-name {
  font-weight: 600;
  font-size: 26px;
  color: #26374e;
  text-align: center;
  margin: 0 0 10px 0;
}

.pricing-price {
  font-weight: 700;
  font-size: 36px;
  color: #26374e;
  margin: 0;
}

.pricing-price span {
  font-weight: 400;
  font-size: 16px;
  color: #496782;
}

.pricing-features {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;

  li {
    position: relative;
    padding-left: 28px;
    font-size: 14px;
    line-height: 1.4;
    color: #26374e;
    display: flex;
    align-items: center;
    text-align: left;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 18px;
      height: 18px;
      background-color: #4a90d9;
      mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
      mask-size: contain;
      mask-repeat: no-repeat;
      -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
      -webkit-mask-size: contain;
      -webkit-mask-repeat: no-repeat;
    }
  }
}

.btn-pricing {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;
  width: calc(100% - 60px);
  height: 56px;
  margin: 0 30px 30px;
  background: #496782;
  border: none;
  border-radius: 9999px;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transition: background 0.2s;

  &:hover {
    background: #3a5269;
  }
}

.btn-pricing-shine {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #496782 0%, #3a5269 100%);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: shine 2s infinite;
  }

  &:hover {
    background: linear-gradient(135deg, #3a5269 0%, #2d4152 100%);
  }
}

@keyframes shine {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

.btn-pricing-filled {
  background: linear-gradient(135deg, #BBCAD8 0%, #496782 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: linear-gradient(135deg, #496782 0%, #3a5269 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(73, 103, 130, 0.4);
  }
}

.btn-pricing-premium {
  background: linear-gradient(135deg, #496782 0%, #26374e 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: linear-gradient(135deg, #3a5269 0%, #1d2a3d 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(38, 55, 78, 0.5);
  }
}

.btn-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.skeleton-text {
  display: inline-block;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  color: transparent;
}

.skeleton-price {
  width: 80px;
  height: 28px;
}

.skeleton-line {
  width: 100%;
  height: 16px;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

// Examples Section
.examples-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  padding: 80px 60px;
  background: #fff;
}

.examples-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  max-width: 700px;
  text-align: center;
}

.section-subtitle {
  font-size: 16px;
  line-height: 1.4;
  color: #26374e;
  margin: 0;
}

.slider-buttons {
  display: flex;
  gap: 15px;
}

.slider-btn {
  width: 42px;
  height: 42px;
  border: 1px solid #496782;
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  img {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: rgba(73, 103, 130, 0.1);
  }
}

.examples-slider {
  display: flex;
  gap: 20px;
  width: 100%;
  max-width: 1320px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.example-card {
  flex-shrink: 0;
  width: 450px;
  height: 400px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  scroll-snap-align: start;
  position: relative;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }

  &:hover .example-card-overlay {
    opacity: 1;
  }
}

.example-card-overlay {
  position: absolute;
  inset: 0;
  background: rgba(38, 55, 78, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s ease;
  border-radius: 20px;
}

// Lightbox
.lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.lightbox-content {
  max-width: min(90vw, 1100px);
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-img {
  max-width: 100%;
  max-height: 88vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.6);
  display: block;
}

.lightbox-close {
  position: fixed;
  top: 20px;
  right: 24px;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 10000;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
}

.lightbox-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.12);
  border: none;
  border-radius: 50%;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 10000;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.lightbox-prev {
  left: 20px;
}

.lightbox-next {
  right: 20px;
}

.lightbox-counter {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
  background: rgba(0, 0, 0, 0.4);
  padding: 5px 14px;
  border-radius: 20px;
  z-index: 10000;
}

// Reviews Section
.reviews-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  padding: 80px 60px;
  background: transparent;
  position: relative;
  z-index: 2;
}

.reviews-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  text-align: center;
}

.reviews-slider {
  display: flex;
  gap: 20px;
  width: 100%;
  max-width: 1320px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.review-card {
  flex-shrink: 0;
  width: 380px;
  min-height: 400px;
  padding: 30px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 30px;
  scroll-snap-align: start;
}

.review-author {
  display: flex;
  align-items: center;
  gap: 10px;
}

.author-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.author-info {
  display: flex;
  flex-direction: column;
}

.author-name {
  font-weight: 600;
  font-size: 16px;
  color: #26374e;
  margin: 0;
}

.author-role {
  font-size: 14px;
  color: #26374e;
  margin: 0;
}

.review-text {
  font-size: 16px;
  line-height: 1.2;
  color: #26374e;
  margin: 0;
}

// CTA Section
.cta-section {
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  padding: 180px 60px 0;
  margin-top: -380px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 0;
}

.cta-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  text-align: center;
  max-width: 1180px;
  padding-top: 150px;
  padding-bottom: 80px;

  h2 {
    font-weight: 600;
    font-size: 50px;
    line-height: 1.2;
    color: #fff;
    margin: 0;
  }
}

.cta-subtitle {
  font-size: 16px;
  line-height: 1.4;
  color: #fff;
  max-width: 700px;
  margin: 0;
}

.cta-buttons {
  display: flex;
  gap: 20px;
}

.btn-secondary-white {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 0 42px;
  min-width: 220px;
  background: #fff;
  border: 2px solid #e0e4e8;
  border-radius: 9999px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  font-weight: 600;
  font-size: 16px;
  text-transform: uppercase;
  color: #496782;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #f5f7f9;
  }
}

// Footer
.footer {
  width: 100%;
  padding: 50px 60px;
  display: flex;
  flex-direction: column;
  gap: 50px;
}

.footer-top {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.footer-logo {
  width: 330px;
  height: 120px;
  object-fit: contain;
}

.footer-company {
  color: #fff;
  font-size: 16px;
  line-height: 1.2;

  p {
    margin: 0;
  }
}

.footer-nav {
  display: flex;
  gap: 100px;
  padding-top: 40px;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 20px;

  a {
    color: #fff;
    font-size: 16px;
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }
}

.footer-contact {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 30px;
}

.footer-login {
  background: #fff;
  color: #496782;
}

.contact-label {
  font-size: 20px;
  color: #bbcad8;
  text-align: right;
  margin: 0;
  line-height: 1.2;
}

.contact-phone {
  font-weight: 600;
  font-size: 30px;
  color: #fff;
  margin: 0;
}

.social-links {
  display: flex;
  gap: 30px;
}

.social-btn {
  width: 50px;
  height: 50px;
  background: #fff;
  border: 0.5px solid #e0e4e8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;

  img {
    width: 30px;
    height: 30px;
  }

  &:hover {
    transform: scale(1.1);
  }
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  color: #fff;
  font-size: 14px;
  gap: 20px;

  p {
    margin: 0;
  }
}

.footer-bottom-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-bottom-link:hover {
  color: #fff;
  text-decoration: underline;
}

.footer-legal {
  margin-top: 15px;
}

.contact-email {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin-top: 5px;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    opacity: 0.8;
  }

  &:hover {
    color: #fff;
    text-decoration: underline;
  }
}

.contact-vk {
  gap: 8px;
  margin-top: 5px;
}

@media (max-width: 1280px) {
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .feature-row {
    gap: 50px;
  }

  .feature-media {
    width: 500px;
    height: 280px;
  }

  .feature-text h3 {
    font-size: 26px;
  }
}

@media (max-width: 1024px) {
  .nav-menu {
    display: none;
  }

  .hero-section {
    height: auto;
    min-height: auto;
    padding-bottom: 160px;
    display: flex;
    flex-direction: column;
  }

  .header {
    order: 1;
    padding: 20px 20px;
    width: 100%;
    z-index: 2;
  }

  .hero-computer {
    order: 2;
    position: relative;
    width: 85%;
    max-width: 85%;
    height: auto;
    right: auto;
    top: auto;
    transform: none;
    margin: 10px auto 30px;
    padding: 0;
    z-index: 2;
  }

  .hero-computer-img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }

  .hero-content-wrapper {
    order: 3;
    padding: 0 20px;
    z-index: 2;
  }

  .hero-content {
    padding-top: 20px;
    max-width: 100%;
    flex-direction: column;
  }

  .hero-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: url('~@/../public/landing/hero-bg.jpg') no-repeat center top;
    background-size: cover;
    z-index: 0;
  }

  .hero-text h1 {
    font-size: 28px;
    text-align: center;
  }

  .hero-subtitle {
    font-size: 15px;
    text-align: center;
  }

  .hero-buttons {
    flex-direction: column;
    align-items: center;
  }

  .hero-buttons .btn-secondary {
    display: none;
  }

  .hero-buttons .btn-primary {
    min-width: 260px;
    height: 48px;
    font-size: 14px;
    padding: 0 25px;
  }

  .btn-primary {
    min-width: 260px;
    height: 48px;
    font-size: 14px;
    padding: 0 25px;
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #496782 0%, #3a5269 100%);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent
      );
      animation: shine 2s infinite;
    }

    &:hover {
      background: linear-gradient(135deg, #3a5269 0%, #2d4152 100%);
    }
  }

  .header-buttons {
    gap: 8px;
  }

  .header-actions {
    gap: 8px;
  }

  .btn-primary-header,
  .btn-secondary-header {
    padding: 0 20px;
    font-size: 12px;
    height: 44px;
  }

  .benefits-section {
    padding: 60px 20px;
  }

  .benefits-title {
    font-size: 28px;
  }

  .benefit-card {
    width: 100%;
  }

  .features-section {
    padding: 60px 30px;
    gap: 60px;
  }

  .feature-row,
  .feature-row-reverse {
    flex-direction: column;
    gap: 30px;
  }

  .feature-row-3d {
    display: flex;
    flex-direction: column;
  }

  .feature-row-3d .feature-media {
    order: 1;
  }

  .feature-row-3d .feature-text {
    order: 2;
  }

  .feature-media {
    width: 100%;
    height: auto;
    aspect-ratio: 640 / 320;
  }

  .feature-text {
    max-width: 100%;
  }

  .feature-text h3 {
    font-size: 24px;
  }

  .pricing-section {
    padding: 60px 20px;
  }

  .section-title {
    font-size: 36px;
  }

  .pricing-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .examples-section {
    padding: 60px 20px;
  }

  .reviews-section {
    padding: 60px 20px;
    background: transparent;
  }

  .example-card {
    width: 300px;
    height: 280px;
  }

  .review-card {
    width: 320px;
    min-height: 350px;
  }

  .cta-section {
    padding: 350px 20px 0;
    margin-top: -250px;
  }

  .cta-content h2 {
    font-size: 36px;
  }

  .cta-buttons {
    flex-direction: column;
    width: 100%;
  }

  .cta-buttons .btn-primary {
    width: 100%;
  }

  .cta-buttons .btn-secondary-white {
    display: none;
  }

  .footer {
    padding: 30px 20px;
  }

  .footer-top {
    flex-direction: column;
    gap: 40px;
  }

  .footer-nav {
    flex-direction: column;
    gap: 30px;
    padding-top: 0;
  }

  .footer-contact {
    align-items: flex-start;
  }

  .contact-label {
    text-align: left;
  }

  .demo-section {
    padding: 60px 30px;
  }

  .demo-content h2 {
    font-size: 36px;
  }

  .demo-subtitle {
    font-size: 18px;
  }

  .demo-btn {
    width: 100%;
    max-width: 320px;
  }
}

@media (max-width: 768px) {
  .header {
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .logo {
    width: 80px !important;
    height: auto;
  }

  .header-actions {
    gap: 6px;
  }

  .header-buttons {
    flex-direction: row;
    width: auto;
    margin-top: 0;
    gap: 8px;
  }

  .btn-text-full {
    display: none;
  }

  .btn-text-mobile {
    display: inline;
  }

  .btn-primary-header,
  .btn-secondary-header {
    width: auto;
    padding: 0 16px;
    height: 36px;
    font-size: 11px;
  }

  .language-switch {
    height: 32px;
    padding: 2px;
  }

  .language-option {
    width: 28px;
    height: 26px;
    font-size: 10px;
  }

  .hero-text h1 {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 16px;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
  }

  .benefits-title {
    font-size: 24px;
  }

  .benefit-content h3 {
    font-size: 18px;
  }

  .benefit-content p {
    font-size: 14px;
  }

  .features-section {
    padding: 40px 20px;
    gap: 40px;
  }

  .feature-text h3 {
    font-size: 20px;
  }

  .feature-text p {
    font-size: 14px;
  }

  .play-button {
    width: 50px;
    height: 50px;
  }

  .demo-section {
    padding: 40px 20px;
  }

  .demo-content h2 {
    font-size: 28px;
  }

  .demo-subtitle {
    font-size: 16px;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .pricing-card {
    min-height: auto;
  }

  .pricing-badge {
    margin-top: -40px;
    font-size: 24px;
  }

  .pricing-name {
    font-size: 24px;
  }

  .section-title {
    font-size: 28px;
  }

  .example-card {
    width: 280px;
    height: 240px;
  }

  .review-card {
    width: 280px;
    min-height: 320px;
  }

  .cta-content h2 {
    font-size: 24px;
  }

  .cta-subtitle {
    font-size: 14px;
  }

  .footer-logo {
    width: 200px;
    height: 80px;
  }

  .contact-phone {
    font-size: 24px;
  }

  .footer-bottom {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px 16px;
    justify-content: center;
    text-align: center;
  }

  .footer-bottom p {
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 400px) {
  .header {
    padding: 14px 10px;
  }

  .logo {
    width: 70px !important;
  }

  .header-actions {
    gap: 4px;
  }

  .header-buttons {
    gap: 4px;
  }

  .btn-primary-header,
  .btn-secondary-header {
    padding: 0 9px;
    font-size: 10px;
  }

  .language-option {
    width: 26px;
  }
}
button.btn-pricing:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pricing-unavailable {
  margin-top: 18px;
  color: #496782;
  font-size: 16px;
}

.pricing-header-premium .pricing-unavailable {
  color: #fff;
}
</style>
